/*!
 * TimbricaFluteSynth — reference-grade virtual flute engine for the browser.
 *
 *   var flute = TimbricaFluteSynth.create({ type: 'concert' });
 *   flute.init();                       // builds the Web Audio graph (idempotent)
 *   await flute.resume();               // MUST be awaited on the first user gesture
 *   flute.noteOn(72, 0.8);  flute.noteOff(72);
 *
 * A flute is NOT a keyboard/percussion/plucked instrument — its whole voice lives
 * in the BREATH. So this engine is built around breath, not a struck string:
 *
 *   TONE     — one bandlimited PeriodicWave per flute type (harmonic stack: strong
 *              fundamental + a moderate 2nd + a steep roll-off — the measured flute
 *              signature). The browser auto-bandlimits a PeriodicWave per playback
 *              frequency, so there is no aliasing even on the top octave.
 *   BREATH   — per-voice filtered noise: a band around the pitch (voiced air) + a
 *              broadband hiss, both shaped by the note envelope. This is what turns a
 *              sine "whistle" into a flute (proven on real spectrograms during design).
 *   CHIFF    — a short breathy attack transient (the tongued "tuh" onset).
 *   VIBRATO  — a shared 4.5–5.5 Hz LFO → osc.detune, that BLOOMS IN ~200 ms after the
 *              attack, the way a real player adds vibrato mid-note.
 *   OVERBLOW — velocity opens a per-voice low-pass (brighter tone) and, on a hard
 *              blow, mixes in an octave partial — the register "push".
 *
 * Two play modes:
 *   POLY (default, forgiving)   — each key = an independent voice (chords, layering).
 *   MONO-LEGATO (authentic)     — one air column: a new key RETUNES the live oscillator
 *                                 with portamento instead of re-attacking (real flute).
 *
 * setBreath(level) drives dynamics from an external source (a microphone) — blow harder
 * → louder + a touch more vibrato. Optional; default expression is full.
 *
 * No external dependencies. Pure Web Audio. Stereo out. ES5-safe (old Safari).
 */
window.TimbricaFluteSynth = (function () {
    'use strict';

    function midiToFreq(midi) { return 440 * Math.pow(2, (midi - 69) / 12); }

    /* ── Flute voices ──
       Each timbre = a harmonic-amplitude profile + breath/attack/vibrato character.
       harm[0] is the fundamental. Profiles are deliberately short (real flutes are
       nearly sinusoidal), which is also why aliasing never bites.
         noise   — level of the voiced air band (breathiness)
         hiss    — level of the broadband breath hiss
         attack  — onset time (s); a fipple recorder speaks fast, a shakuhachi slow
         release — note tail (s)
         vibRate — vibrato LFO frequency (Hz)
         vibCents— vibrato depth (cents) once bloomed in
         bright  — timbre-brightness bias for the overblow low-pass (1 = silvery, <1 dark)
         octave  — how much octave partial a hard blow adds
         breathQ — Q of the voiced-air band-pass (low Q = airier)                        */
    var FLUTE_TYPES = {
        concert: {
            harm: [1.0, 0.42, 0.12, 0.06, 0.025, 0.012, 0.006],
            noise: 0.05, hiss: 0.020, attack: 0.045, release: 0.14,
            vibRate: 5.5, vibCents: 22, bright: 1.00, octave: 0.12, breathQ: 5
        },
        recorder: {
            harm: [1.0, 0.20, 0.09, 0.03, 0.010],
            noise: 0.04, hiss: 0.018, attack: 0.020, release: 0.10,
            vibRate: 5.5, vibCents: 9, bright: 0.92, octave: 0.06, breathQ: 6
        },
        bansuri: {
            harm: [1.0, 0.38, 0.11, 0.045, 0.018, 0.008],
            noise: 0.11, hiss: 0.050, attack: 0.060, release: 0.17,
            vibRate: 5.0, vibCents: 30, bright: 0.90, octave: 0.10, breathQ: 3.5
        },
        pan: {
            harm: [1.0, 0.30, 0.16, 0.06, 0.020, 0.010],
            noise: 0.17, hiss: 0.085, attack: 0.028, release: 0.12,
            vibRate: 5.2, vibCents: 16, bright: 1.05, octave: 0.08, breathQ: 3
        },
        shakuhachi: {
            harm: [1.0, 0.26, 0.07, 0.02, 0.008],
            noise: 0.19, hiss: 0.095, attack: 0.070, release: 0.19,
            vibRate: 4.5, vibCents: 34, bright: 0.62, octave: 0.09, breathQ: 2.5
        },
        native: {
            harm: [1.0, 0.28, 0.09, 0.03, 0.012],
            noise: 0.09, hiss: 0.040, attack: 0.050, release: 0.16,
            vibRate: 5.0, vibCents: 20, bright: 0.82, octave: 0.08, breathQ: 4
        }
    };
    var TYPE_ORDER = ['concert', 'recorder', 'bansuri', 'pan', 'shakuhachi', 'native'];

    /* ───────────────────────── factory ───────────────────────── */
    function create(opts) {
        opts = opts || {};
        var inst = {};

        inst.type = FLUTE_TYPES[opts.type] ? opts.type : 'concert';
        inst.ctx = null;
        inst._extCtx = opts.context || null;    /* offline/test context injection */
        inst.initialized = false;
        inst._audioFailed = false;

        /* runtime state */
        inst.mode = 'poly';                      /* 'poly' | 'mono' */
        inst.activeNotes = {};                   /* midi → handle (poly) */
        inst.heldOrder = [];                     /* midi[] press order (mono legato stack) */
        inst.monoVoice = null;                   /* the single live handle in mono */
        inst.sustainOn = false;
        inst.pendingReleases = {};
        inst.portamento = 0.045;                 /* mono glide time (s) */
        inst.vibratoOn = true;
        inst.reverbAmount = 0.22;
        inst.maxVoices = 12;                     /* poly cap (voice-stealing) */
        inst._waves = {};                        /* type → PeriodicWave (cached) */
        inst._breathExtern = false;              /* true while a mic drives expression */

        /* ─────────────── init ─────────────── */
        inst.init = function () {
            if (inst.initialized || inst._audioFailed) return inst;
            var ctx;
            if (inst._extCtx) {
                ctx = inst.ctx = inst._extCtx;               /* offline render / test */
            } else {
                var Ctor = window.AudioContext || window.webkitAudioContext;
                if (!Ctor) { inst._audioFailed = true; return inst; }
                /* timbrica-core may install a SafeAC shim that THROWS when there is no
                   real Web Audio. Swallow once so it never spams or breaks the page. */
                try {
                    ctx = inst.ctx = new Ctor({ latencyHint: 'interactive' });
                } catch (e) { inst._audioFailed = true; return inst; }
            }

            /* ── master → expression → limiter → analyser → out ── */
            inst.voiceBus = ctx.createGain();
            inst.voiceBus.gain.value = 1.0;

            inst.master = ctx.createGain();
            inst.master.gain.value = 0.85;

            /* expression / swell — where the microphone breath rides (default: full) */
            inst.expr = ctx.createGain();
            inst.expr.gain.value = 1.0;

            /* soft brick-wall limiter so a full chord + reverb cannot clip */
            var lim = ctx.createDynamicsCompressor();
            lim.threshold.value = -2.0; lim.knee.value = 0; lim.ratio.value = 20;
            lim.attack.value = 0.003; lim.release.value = 0.10;
            inst.limiter = lim;

            inst.analyser = ctx.createAnalyser();
            inst.analyser.fftSize = 2048;
            inst.analyser.smoothingTimeConstant = 0.78;

            inst.voiceBus.connect(inst.master);
            inst.master.connect(inst.expr);
            inst.expr.connect(lim);
            lim.connect(inst.analyser);
            inst.analyser.connect(ctx.destination);

            /* recorder tap (post-limiter, so a WAV matches what you hear) */
            try {
                inst.recDest = ctx.createMediaStreamDestination();
                inst.analyser.connect(inst.recDest);
            } catch (e) { inst.recDest = null; }

            /* ── concert-hall reverb send ── */
            inst.reverbSend = ctx.createGain();
            inst.reverbConv = ctx.createConvolver();
            inst.reverbWet = ctx.createGain();
            inst.reverbConv.buffer = _genIR(ctx, 2.4, 2.6);
            inst.voiceBus.connect(inst.reverbSend);
            inst.reverbSend.connect(inst.reverbConv);
            inst.reverbConv.connect(inst.reverbWet);
            inst.reverbWet.connect(inst.master);
            inst.reverbWet.gain.value = inst.reverbAmount;

            /* ── shared vibrato LFO (fanned out to each voice's osc.detune) ── */
            inst.vibLfo = ctx.createOscillator();
            inst.vibLfo.type = 'sine';
            inst.vibLfo.frequency.value = FLUTE_TYPES[inst.type].vibRate;
            try { inst.vibLfo.start(); } catch (e) {}

            inst.initialized = true;
            return inst;
        };

        /* Resume the context. Returns a Promise — AWAIT IT on the first user gesture,
           otherwise the first note is silent on strict-autoplay browsers (Safari/
           mobile). This is the #1 cause of "I click and nothing happens". */
        inst.resume = function () {
            inst.init();
            if (!inst.ctx || inst._audioFailed) return Promise.resolve(false);
            if (inst.ctx.state === 'running') return Promise.resolve(true);
            try {
                return inst.ctx.resume().then(function () {
                    return inst.ctx.state === 'running';
                }).catch(function () { return false; });
            } catch (e) { return Promise.resolve(false); }
        };
        inst.isReady = function () { return !!inst.ctx && inst.ctx.state === 'running'; };
        inst.audioFailed = function () { return !!inst._audioFailed; };

        /* ─────────────── note on ─────────────── */
        inst.noteOn = function (midi, velocity) {
            inst.init();
            if (!inst.ctx || inst._audioFailed) return null;
            velocity = _clampVel(velocity);

            if (inst.mode === 'mono') return _monoNoteOn(inst, midi, velocity);

            if (inst.activeNotes[midi]) inst._hardKill(midi);   /* re-trigger */
            _steal(inst);                                        /* cap polyphony */
            var h = _fluteVoiceOn(inst, midi, velocity);
            if (!h) return null;
            inst.activeNotes[midi] = h;
            inst.heldOrder.push(midi);
            return h;
        };

        /* ─────────────── note off (with sustain) ─────────────── */
        inst.noteOff = function (midi) {
            if (!inst.ctx) return;
            if (inst.mode === 'mono') return _monoNoteOff(inst, midi);
            if (!inst.activeNotes[midi]) return;
            if (inst.sustainOn) { inst.pendingReleases[midi] = true; return; }
            _release(inst, midi);
        };

        inst.setSustain = function (on) {
            inst.sustainOn = !!on;
            if (!on) {
                for (var m in inst.pendingReleases) {
                    if (inst.pendingReleases.hasOwnProperty(m) && inst.activeNotes[m]) _release(inst, parseInt(m, 10));
                }
                inst.pendingReleases = {};
            }
        };

        inst.stopAll = function () {
            for (var m in inst.activeNotes) {
                if (inst.activeNotes.hasOwnProperty(m)) _killHandle(inst, inst.activeNotes[m]);
            }
            inst.activeNotes = {}; inst.heldOrder = []; inst.pendingReleases = {};
            inst.monoVoice = null;
        };
        inst.panic = inst.stopAll;

        inst._hardKill = function (midi) {
            var h = inst.activeNotes[midi];
            if (!h) return;
            _killHandle(inst, h);
            delete inst.activeNotes[midi];
            var i = inst.heldOrder.indexOf(midi);
            if (i >= 0) inst.heldOrder.splice(i, 1);
        };

        /* ─────────────── setters ─────────────── */
        inst.setType = function (t) {
            if (!FLUTE_TYPES[t] || t === inst.type) return;
            var held = inst.heldOrder.slice();
            inst.stopAll();
            inst.type = t;
            if (inst.vibLfo) {
                try { inst.vibLfo.frequency.setTargetAtTime(FLUTE_TYPES[t].vibRate, inst.ctx.currentTime, 0.02); } catch (e) {}
            }
            for (var i = 0; i < held.length; i++) inst.noteOn(held[i], 0.7);
        };

        inst.setMode = function (m) {
            if (m !== 'poly' && m !== 'mono') return;
            if (m === inst.mode) return;
            var held = inst.heldOrder.slice();
            inst.stopAll();
            inst.mode = m;
            for (var i = 0; i < held.length; i++) inst.noteOn(held[i], 0.7);
        };

        inst.setVibrato = function (on, cents) {
            inst.vibratoOn = !!on;
            if (typeof cents === 'number') inst._vibCentsOverride = cents;
        };
        inst.setPortamento = function (sec) { inst.portamento = Math.max(0, Math.min(0.4, sec)); };

        inst.setReverb = function (amount) {
            inst.reverbAmount = Math.max(0, Math.min(1, amount));
            if (inst.reverbWet) inst.reverbWet.gain.setTargetAtTime(inst.reverbAmount, inst.ctx.currentTime, 0.05);
        };

        inst.setVolume = function (v) {
            if (!inst.master) return;
            v = Math.max(0, Math.min(1, v));
            inst.master.gain.setTargetAtTime(v * 0.85, inst.ctx.currentTime, 0.02);
        };

        /* External breath (microphone). level 0..1 → expression gain + a vibrato nudge.
           Call continuously while a mic is armed; call setBreathExtern(false) to release
           control back to full expression. */
        inst.setBreathExtern = function (on) {
            inst._breathExtern = !!on;
            if (!on && inst.expr) inst.expr.gain.setTargetAtTime(1.0, inst.ctx.currentTime, 0.08);
        };
        inst.setBreath = function (level) {
            if (!inst.expr || !inst._breathExtern) return;
            level = Math.max(0, Math.min(1, level));
            /* keep a small floor so a barely-audible breath still speaks */
            inst.expr.gain.setTargetAtTime(0.06 + level * 0.94, inst.ctx.currentTime, 0.03);
        };

        inst.getAnalyser = function () { return inst.analyser || null; };
        inst.getRecorderStream = function () { return inst.recDest ? inst.recDest.stream : null; };
        inst.getContext = function () { return inst.ctx || null; };
        inst.getType = function () { return inst.type; };

        /* ═══════════ per-voice construction ═══════════ */
        function _fluteVoiceOn(inst, midi, vel) {
            var ctx = inst.ctx, f = midiToFreq(midi), t = ctx.currentTime;
            var T = FLUTE_TYPES[inst.type];

            /* 1) tone — one bandlimited PeriodicWave oscillator */
            var osc = ctx.createOscillator();
            osc.setPeriodicWave(_waveFor(inst, inst.type));
            osc.frequency.value = f;

            /* 2) per-voice overblow low-pass (velocity opens the tube) */
            var lp = ctx.createBiquadFilter();
            lp.type = 'lowpass';
            lp.frequency.value = _cutoff(midi, vel, T);
            lp.Q.value = 0.4 + vel * 0.3;

            /* 3) note envelope + stereo placement */
            var noteGain = ctx.createGain();
            var pan = ctx.createStereoPanner ? ctx.createStereoPanner() : ctx.createGain();
            if (pan.pan) pan.pan.value = Math.max(-0.3, Math.min(0.3, (midi - 74) / 52));

            osc.connect(lp); lp.connect(noteGain); noteGain.connect(pan); pan.connect(inst.voiceBus);

            /* 4) octave overblow shimmer — only on a hard blow */
            var oct = null, octG = null;
            if (vel > 0.6 && T.octave > 0) {
                oct = ctx.createOscillator(); oct.type = 'sine';
                oct.frequency.value = f * 2;
                octG = ctx.createGain(); octG.gain.value = (vel - 0.6) * T.octave;
                oct.connect(octG); octG.connect(lp);
                try { oct.start(t); } catch (e) {}
            }

            /* 5) breath — voiced air band + broadband hiss, shaped by the note env */
            var breath = ctx.createBufferSource();
            breath.buffer = _breathNoiseBuf(ctx); breath.loop = true;
            var bp = ctx.createBiquadFilter(); bp.type = 'bandpass';
            bp.frequency.value = Math.min(9000, f * 2); bp.Q.value = T.breathQ;
            var bpG = ctx.createGain(); bpG.gain.value = T.noise * (0.6 + vel * 0.6);
            var hp = ctx.createBiquadFilter(); hp.type = 'highpass'; hp.frequency.value = 2600;
            var hpG = ctx.createGain(); hpG.gain.value = T.hiss * (0.5 + vel * 0.5);
            breath.connect(bp); bp.connect(bpG); bpG.connect(noteGain);
            breath.connect(hp); hp.connect(hpG); hpG.connect(noteGain);
            try { breath.start(t); } catch (e) {}

            /* 6) attack chiff — a short breathy onset transient */
            _breathChiff(inst, noteGain, f, vel);

            /* 7) vibrato — shared LFO, per-voice depth that blooms in after the attack */
            var vibG = ctx.createGain(); vibG.gain.value = 0;
            var depth = inst.vibratoOn ? (inst._vibCentsOverride != null ? inst._vibCentsOverride : T.vibCents) : 0;
            if (inst.vibLfo && depth > 0) {
                inst.vibLfo.connect(vibG); vibG.connect(osc.detune);
                if (oct) vibG.connect(oct.detune);
                vibG.gain.setValueAtTime(0.0001, t);
                vibG.gain.setTargetAtTime(depth, t + 0.18, 0.14);
            }

            /* 8) envelope — soft breathy onset, HOLD (no decay: a wind note sustains) */
            var atk = T.attack + 0.03 * (1 - vel);
            var peak = 0.16 + vel * 0.12;
            noteGain.gain.setValueAtTime(0.0001, t);
            noteGain.gain.exponentialRampToValueAtTime(peak, t + atk);

            try { osc.start(t); } catch (e) {}

            return {
                midi: midi, osc: osc, oct: oct, octG: octG, lp: lp, noteGain: noteGain,
                pan: pan, breath: breath, bp: bp, bpG: bpG, hp: hp, hpG: hpG, vibG: vibG,
                vel: vel, T: T, releasing: false
            };
        }

        /* ═══════════ mono-legato ═══════════ */
        function _monoNoteOn(inst, midi, vel) {
            var now = inst.ctx.currentTime, f = midiToFreq(midi), T = FLUTE_TYPES[inst.type];
            /* de-dup in the held stack, newest wins */
            var ex = inst.heldOrder.indexOf(midi);
            if (ex >= 0) inst.heldOrder.splice(ex, 1);
            inst.heldOrder.push(midi);

            if (inst.monoVoice && !inst.monoVoice.releasing) {
                var h = inst.monoVoice;                       /* LEGATO: glide, no re-attack */
                h.osc.frequency.setTargetAtTime(f, now, inst.portamento);
                if (h.oct) h.oct.frequency.setTargetAtTime(f * 2, now, inst.portamento);
                h.lp.frequency.setTargetAtTime(_cutoff(midi, h.vel, T), now, 0.04);
                if (h.bp) h.bp.frequency.setTargetAtTime(Math.min(9000, f * 2), now, inst.portamento);
                h.midi = midi;
                return h;
            }
            inst.monoVoice = _fluteVoiceOn(inst, midi, vel);   /* first note: real (tongued) attack */
            inst.activeNotes[midi] = inst.monoVoice;
            return inst.monoVoice;
        }

        function _monoNoteOff(inst, midi) {
            var i = inst.heldOrder.indexOf(midi);
            if (i >= 0) inst.heldOrder.splice(i, 1);
            if (!inst.monoVoice) return;
            if (inst.sustainOn && inst.heldOrder.length === 0) { inst.pendingReleases[midi] = true; return; }
            if (inst.heldOrder.length > 0) {
                var prev = inst.heldOrder[inst.heldOrder.length - 1];  /* fall back to held key */
                var f = midiToFreq(prev), now = inst.ctx.currentTime, h = inst.monoVoice;
                h.osc.frequency.setTargetAtTime(f, now, inst.portamento);
                if (h.oct) h.oct.frequency.setTargetAtTime(f * 2, now, inst.portamento);
                if (h.bp) h.bp.frequency.setTargetAtTime(Math.min(9000, f * 2), now, inst.portamento);
                h.midi = prev;
            } else {
                _fadeStop(inst, inst.monoVoice, inst.monoVoice.T.release);
                inst.monoVoice = null; inst.activeNotes = {};
            }
        }

        /* ═══════════ release / kill ═══════════ */
        function _release(inst, midi) {
            var h = inst.activeNotes[midi];
            if (!h || h.releasing) return;
            _fadeStop(inst, h, h.T.release);
            delete inst.activeNotes[midi];
            var idx = inst.heldOrder.indexOf(midi);
            if (idx >= 0) inst.heldOrder.splice(idx, 1);
        }

        function _killHandle(inst, h) {
            if (!h) return;
            _fadeStop(inst, h, 0.03);
        }

        /* Ramp a voice's gain to silence over `rel`, stop its sources, deferred cleanup. */
        function _fadeStop(inst, h, rel) {
            if (h.releasing) return;
            h.releasing = true;
            var ctx = inst.ctx, t = ctx.currentTime;
            try {
                h.noteGain.gain.cancelScheduledValues(t);
                h.noteGain.gain.setValueAtTime(Math.max(h.noteGain.gain.value, 0.0001), t);
                h.noteGain.gain.exponentialRampToValueAtTime(0.0002, t + rel);
            } catch (e) {}
            var stop = t + rel + 0.05;
            _try(function () { h.osc.stop(stop); });
            if (h.oct) _try(function () { h.oct.stop(stop); });
            if (h.breath) _try(function () { h.breath.stop(stop); });
            setTimeout(function () { _disconnectHandle(h); }, (rel + 0.12) * 1000);
        }

        function _disconnectHandle(h) {
            var nodes = [h.osc, h.oct, h.octG, h.lp, h.breath, h.bp, h.bpG, h.hp, h.hpG, h.vibG, h.noteGain, h.pan];
            for (var j = 0; j < nodes.length; j++) { if (nodes[j]) { try { nodes[j].disconnect(); } catch (e) {} } }
        }

        /* Oldest-first voice stealing so mobile can't drown in oscillators. */
        function _steal(inst) {
            var keys = inst.heldOrder;
            while (keys.length >= inst.maxVoices) {
                var victim = keys[0];
                if (inst.activeNotes[victim]) inst._hardKill(victim);
                else keys.shift();
            }
        }

        function _breathChiff(inst, dest, freq, vel) {
            var ctx = inst.ctx, t = ctx.currentTime;
            var n = ctx.createBufferSource(); n.buffer = _noiseBuf(ctx);
            var bp = ctx.createBiquadFilter(); bp.type = 'bandpass';
            bp.frequency.value = Math.min(8000, freq * 3); bp.Q.value = 0.8;
            var g = ctx.createGain();
            var amp = 0.06 + vel * 0.06;
            g.gain.setValueAtTime(amp, t);
            g.gain.exponentialRampToValueAtTime(0.0005, t + 0.05);
            n.connect(bp); bp.connect(g); g.connect(dest);
            _try(function () { n.start(t); n.stop(t + 0.07); });
        }

        function _waveFor(inst, type) {
            if (inst._waves[type]) return inst._waves[type];
            var spec = FLUTE_TYPES[type].harm, N = spec.length;
            var real = new Float32Array(N + 1), imag = new Float32Array(N + 1);
            var peak = 0, h;
            for (h = 0; h < N; h++) { imag[h + 1] = spec[h]; peak += Math.abs(spec[h]); }
            if (peak > 0) for (h = 1; h <= N; h++) imag[h] /= peak;
            var w = inst.ctx.createPeriodicWave(real, imag, { disableNormalization: false });
            inst._waves[type] = w;
            return w;
        }

        return inst;
    }

    /* ────────── static helpers ────────── */
    function _clampVel(v) { if (typeof v !== 'number') v = 0.75; if (v < 0.05) v = 0.05; if (v > 1) v = 1; return v; }
    function _try(fn) { try { fn(); } catch (e) {} }

    /* velocity/register → overblow low-pass cutoff (never darker than the 2nd harmonic) */
    function _cutoff(midi, vel, T) {
        var f = midiToFreq(midi);
        var c = f * 2 + (1500 + vel * 7000) * (T.bright || 1);
        return Math.min(c, 18000);
    }

    /* short white-noise buffer (0.2 s) for the attack chiff */
    var _NOISE = null;
    function _noiseBuf(ctx) {
        if (_NOISE && _NOISE.sampleRate === ctx.sampleRate) return _NOISE;
        var len = Math.floor(ctx.sampleRate * 0.2);
        var b = ctx.createBuffer(1, len, ctx.sampleRate), d = b.getChannelData(0);
        var s = 22222;
        for (var i = 0; i < len; i++) { s = (s * 1103515245 + 12345) & 0x7fffffff; d[i] = (s / 0x3fffffff) - 1; }
        _NOISE = b; return b;
    }
    /* longer noise (2 s) for the LOOPING breath bed — long enough that the loop point
       is inaudible (a 0.2 s loop would buzz at 5 Hz). */
    var _BREATH = null;
    function _breathNoiseBuf(ctx) {
        if (_BREATH && _BREATH.sampleRate === ctx.sampleRate) return _BREATH;
        var len = Math.floor(ctx.sampleRate * 2.0);
        var b = ctx.createBuffer(1, len, ctx.sampleRate), d = b.getChannelData(0);
        var s = 8675309;
        for (var i = 0; i < len; i++) { s = (s * 1103515245 + 12345) & 0x7fffffff; d[i] = (s / 0x3fffffff) - 1; }
        _BREATH = b; return b;
    }

    /* stereo decaying-noise impulse response → convolution reverb (concert hall). */
    function _genIR(ctx, duration, decay) {
        var sr = ctx.sampleRate, len = Math.max(1, Math.floor(sr * duration));
        var ir = ctx.createBuffer(2, len, sr);
        var s = 99991;
        function rnd() { s = (s * 1103515245 + 12345) & 0x7fffffff; return (s / 0x3fffffff) - 1; }
        for (var ch = 0; ch < 2; ch++) {
            var d = ir.getChannelData(ch), lp = 0;
            for (var i = 0; i < len; i++) {
                var tt = i / len, env = Math.pow(1 - tt, decay), w = rnd() * env, a = 0.2 + 0.7 * tt;
                lp = lp + a * (w - lp); d[i] = lp;
            }
            var taps = [0.013, 0.021, 0.033, 0.049, 0.063];
            for (var k = 0; k < taps.length; k++) {
                var pos = Math.floor(taps[k] * sr);
                if (pos < len) d[pos] += (0.5 - k * 0.08) * (ch ? -1 : 1);
            }
        }
        return ir;
    }

    return {
        create: create,
        midiToFreq: midiToFreq,
        FLUTE_TYPES: FLUTE_TYPES,
        TYPE_ORDER: TYPE_ORDER
    };
})();
